import type React from 'react';
import { Card, CardContent, Typography, Box, Rating, Stack, Button } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LocationOn } from '@mui/icons-material';
import { ICompany } from '../../redux/company/types';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RichTextViewer } from '../Common/RichTextViewer';
import { ROUTES } from '../../types/global/routes.types';
import { useDispatch } from 'react-redux';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { navigate } from '../../redux/navigator/action';

interface CompanyCardProps {
    data: ICompany;
    enableHeader?: boolean;
}

export const CompanyCard: React.FC<CompanyCardProps> = ({ data, enableHeader }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    const handleCompanyClick = () => {
        if (data?.slug) {
            const profileRoute = replaceRouteParam(ROUTES.COMPANY, 'id', data.slug);
            dispatch(navigate({ routes: profileRoute }));
        }
    };

    return (
        <Box>
            {enableHeader && (
                <Box borderBottom={1}>
                    <Box py={1} display={'flex'} gap={2}>
                        <AttachFileIcon
                            sx={{
                                color: theme[themeStyle].tag_white,
                            }}
                        />
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 19,
                                color: theme[themeStyle].tag_white,
                            }}>
                            Linked Company
                        </Typography>
                    </Box>
                </Box>
            )}
            <Card
                sx={{
                    maxWidth: {
                        xs: '100%',
                        md: 600,
                    },
                    margin: 'auto',
                    boxShadow: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: theme[themeStyle].secondaryBg,
                    mt: 1,
                }}>
                {/* Header Section with Profile Picture and Basic Info */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <CompanyAvatar company={data} enableDefaultNavigation />

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                fontWeight="bold"
                                sx={{
                                    mb: 1,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 16,
                                }}>
                                {data.company_name}
                            </Typography>

                            {/* Rating Section */}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Rating
                                    value={data.rating}
                                    precision={0.1}
                                    readOnly
                                    size="small"
                                    sx={{
                                        '& .MuiRating-iconFilled': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '& .MuiRating-iconHover': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '& .MuiRating-iconEmpty': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mb: 1,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                    }}>
                                    {data?.rating?.toFixed(1)} ({data?.number_of_reviews}{' '}
                                    {data?.number_of_reviews === 1 ? 'review' : 'reviews'})
                                </Typography>
                            </Stack>

                            {/* Location */}
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <LocationOn
                                    fontSize="small"
                                    color="action"
                                    sx={{ color: theme[themeStyle].tag_white }}
                                />
                                <Typography
                                    sx={{
                                        mb: 1,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                    }}>
                                    {data.location.address}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                {/* About Section */}
                <CardContent sx={{ pt: 0 }}>
                    <Typography
                        sx={{
                            mb: 1,
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                        }}
                        gutterBottom
                        fontWeight="medium">
                        About
                    </Typography>

                    <RichTextViewer
                        content={data.about}
                        textStyle={{
                            padding: 0,
                            fontSize: 12,
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}
                    />
                </CardContent>
                <Button
                    variant="text"
                    size="small"
                    sx={{ marginTop: 1, textTransform: 'none', p: 2 }}
                    color="primary"
                    onClick={handleCompanyClick}>
                    View Company Details
                </Button>
            </Card>
        </Box>
    );
};
